import { Injectable } from '@angular/core';
import { Observable, map, shareReplay } from 'rxjs';

import { IAPIListRequestOptions, IAPIListResponse, IBrowseLinkApiResponse } from '@sondermind/utilities/models-http';

import { BrowseStateHttpService } from '../services/browse-state.httpservice';

@Injectable({
  providedIn: 'root'
})
export class BrowseLinksStore {
  private browseLinksObservables: Map<string, Observable<IAPIListResponse<IBrowseLinkApiResponse>>> = new Map();
  private browseCityLinksObservables: Map<string, Observable<IAPIListResponse<IBrowseLinkApiResponse>>> = new Map();

  constructor(private browseStateHttpService: BrowseStateHttpService) {}

  /**
   * Get browse links for a specific state, using cache if available
   * @param state - The state abbreviation
   * @param forceRefresh - Whether to force a refresh from the API
   * @returns Observable of browse links data
   */
  getBrowseLinks$(state: string, forceRefresh: boolean = false): Observable<IAPIListResponse<IBrowseLinkApiResponse>> {
    // If forcing refresh, clear the cached observable
    if (forceRefresh) {
      this.browseLinksObservables.delete(state);
    }

    // Return cached observable if available
    if (this.browseLinksObservables.has(state)) {
      return this.browseLinksObservables.get(state) as Observable<IAPIListResponse<IBrowseLinkApiResponse>>;
    }

    // Create new observable and cache it
    const stateOptions: IAPIListRequestOptions = {
      page: 'disabled' as const,
      filter: { key: 'state', value: state },
    };

    const observable = this.browseStateHttpService.getBrowseLinks(stateOptions).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.browseLinksObservables.set(state, observable);
    return observable;
  }

  /**
   * Get browse city links for a specific state and city, using cache if available
   * @param state - The state abbreviation
   * @param city - The city name
   * @param forceRefresh - Whether to force a refresh from the API
   * @returns Observable of browse city links data
   */
  getBrowseCityLinks$(
    state: string,
    city: string,
    forceRefresh: boolean = false
  ): Observable<IAPIListResponse<IBrowseLinkApiResponse>> {
    const cacheKey = `${state}-${city}`;

    // If forcing refresh, clear the cached observable
    if (forceRefresh) {
      this.browseCityLinksObservables.delete(cacheKey);
    }

    // Return cached observable if available
    if (this.browseCityLinksObservables.has(cacheKey)) {
      return this.browseCityLinksObservables.get(cacheKey) as Observable<IAPIListResponse<IBrowseLinkApiResponse>>;
    }

    // Create new observable and cache it
    const cityOptions: IAPIListRequestOptions = {
      page: 'disabled' as const,
      filter: [
        { key: 'state', value: state },
        { key: 'city', value: city }
      ],
    };

    const observable = this.browseStateHttpService.getBrowseCityLinks(cityOptions).pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.browseCityLinksObservables.set(cacheKey, observable);
    return observable;
  }

  /**
   * Get available cities for a specific state
   * @param state - The state abbreviation
   * @param forceRefresh - Whether to force a refresh from the API
   * @returns Observable of available cities
   */
  getAvailableCities$(state: string, forceRefresh: boolean = false): Observable<string[]> {
    return this.getBrowseLinks$(state, forceRefresh).pipe(
      map((response) => {
        const cities = response.data
          .filter((item) => item.city)
          .map((item) => item.city as string);
        return [...new Set(cities.map((city) => city.toLowerCase()))];
      })
    );
  }

  /**
   * Check if a city exists in a specific state
   * @param state - The state abbreviation
   * @param city - The city name to check
   * @param forceRefresh - Whether to force a refresh from the API
   * @returns Observable of boolean indicating if city exists
   */
  isCityValid$(state: string, city: string, forceRefresh: boolean = false): Observable<boolean> {
    return this.getAvailableCities$(state, forceRefresh).pipe(
      map((availableCities) => availableCities.includes(city.toLowerCase()))
    );
  }

  /**
   * Clear the cache for a specific state or all states
   * @param state - Optional state to clear cache for. If not provided, clears all cache.
   */
  clearCache(state?: string): void {
    if (state) {
      this.browseLinksObservables.delete(state);
      // Clear city links cache for this state
      const keysToDelete = Array.from(this.browseCityLinksObservables.keys())
        .filter((key) => key.startsWith(`${state}-`));
      keysToDelete.forEach((key) => this.browseCityLinksObservables.delete(key));
    } else {
      this.browseLinksObservables.clear();
      this.browseCityLinksObservables.clear();
    }
  }
}
